import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthAction } from '../../store/auth/auth.action';
import { get as _get } from 'lodash';

interface UseInactivityTimeoutOptions {
  timeoutMinutes?: number;
  warningSeconds?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
}

/**
 * Hook to track user inactivity and automatically logout after specified time.
 * Tracks UI activity (mouse moves, clicks, keyboard input, touches, scrolls).
 * 
 * @param options Configuration options
 * @param options.timeoutMinutes Minutes of inactivity before logout (default: 15)
 * @param options.warningSeconds Seconds before timeout to show warning (default: 60)
 * @param options.onWarning Callback when warning threshold is reached
 * @param options.onTimeout Callback when timeout is reached
 */
export const useInactivityTimeout = ({
  timeoutMinutes = 15,
  warningSeconds = 60,
  onWarning,
  onTimeout
}: UseInactivityTimeoutOptions = {}) => {
  const dispatch = useDispatch();
  const authentication = useSelector((state: any) => 
    _get(state, ['auth', 'authentication'], null)
  );
  const isAuthenticated = authentication !== null && authentication.access_token;

  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const showWarningRef = useRef<boolean>(false);

  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  const WARNING_MS = TIMEOUT_MS - (warningSeconds * 1000);

  // Keep ref in sync with state
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  const clearAllTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const startCountdown = () => {
    console.log(`[InactivityTimeout] ⚠️ WARNING THRESHOLD REACHED - Starting countdown from ${warningSeconds} seconds`);
    setSecondsRemaining(warningSeconds);
    setShowWarning(true);
    
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        }
        return newValue;
      });
    }, 1000);
  };

  const handleLogout = () => {
    clearAllTimers();
    setShowWarning(false);
    if (onTimeout) {
      onTimeout();
    }
    dispatch(AuthAction.logout());
  };

  const resetTimer = () => {
    if (!isAuthenticated) {
      return;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    lastActivityRef.current = now;
    
    // Hide warning if it was showing - use ref to get current value
    if (showWarningRef.current) {
      setShowWarning(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    clearAllTimers();

    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      if (onWarning) {
        onWarning();
      }
      startCountdown();
    }, WARNING_MS);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, TIMEOUT_MS);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }


    // Activity events to track
    const events = [
      'mousedown',
      'keydown',
      'keyup',
      'touchstart',
      'click'
    ];

    // Throttle activity detection to avoid excessive resets
    let activityThrottleTimeout: NodeJS.Timeout | null = null;
    let activityCount = 0;
    const handleActivity = (event: Event) => {
      activityCount++;
      
      // Log keyboard events for debugging
      if (event.type === 'keydown' || event.type === 'keyup') {
        const keyEvent = event as KeyboardEvent;
        // console.log(`[InactivityTimeout] 🎹 Keyboard activity: ${event.type} - Key: ${keyEvent.key}, Code: ${keyEvent.code}`);
      }
      
      // If warning is showing, reset immediately without throttle
      if (showWarningRef.current) {
        // console.log(`[InactivityTimeout] 👆 URGENT: User activity during warning (${event.type}) - Resetting immediately`);
        if (activityThrottleTimeout) {
          clearTimeout(activityThrottleTimeout);
          activityThrottleTimeout = null;
        }
        resetTimer();
        return;
      }
      
      // Otherwise use throttle for normal activity
      if (!activityThrottleTimeout) {
        // console.log(`[InactivityTimeout] 👆 User activity detected (${event.type}) - Activity count: ${activityCount}`);
        activityThrottleTimeout = setTimeout(() => {
          resetTimer();
          activityThrottleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      clearAllTimers();
      if (activityThrottleTimeout) {
        clearTimeout(activityThrottleTimeout);
      }
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated]);

  return {
    showWarning,
    secondsRemaining,
    resetTimer,
    handleLogout
  };
};
